// Contenido principal de la vista de propiedad

import React, { useState, useEffect } from 'react';
import { getTimeSince, getPropertyStatus } from '../utils/propertyHelpers';
import { getLastChanceEndDate, getLastChanceBidRange, canUserBidInLastChance } from '../config/lastChanceConfig';
import { getBlindBiddingEndDate, canUserBlindBid } from '../config/blindBiddingConfig';

// Imports de componentes modulares de PropertyView
import { LastChanceBanner } from './PropertyView/LastChanceBanner';
import { HighestBidCard, UserLastBidCard, UpcomingBidCard, UserBlindOfferCard } from './PropertyView/BidCards';
import { BidFormCard } from './PropertyView/BidFormCard';
import { ScheduleVisitCard } from './PropertyView/ScheduleVisitCard';
import { InfoCard } from './PropertyView/InfoCard';
import { ClosedStatusCard } from './PropertyView/ClosedStatusCard';
import { PropertyDetailsTabs } from './PropertyView/PropertyDetailsTabs';
import PropertyMedia from './PropertyMedia';
import DocumentViewerModal from './DocumentViewerModal';
import { BuyerProfileCard } from './buyer/BuyerProfileCard';
import BuyerProfileWizard from './buyer/BuyerProfileWizard';
import BlindBidCard from './BlindBidCard';
import BlindBidStatusCard from './BlindBidStatusCard';

/**
 * Componente que renderiza todo el contenido visual de la propiedad
 * Incluye: tarjetas de ofertas, formulario, media, detalles e información
 */
export const PropertyContent = ({
  property,
  propertyBids,
  hasBids,
  currentPrice,
  highestBid,
  userLastBid,
  isTopBidder,
  isClosed,
  isUpcoming,
  invite,
  buyerProfile,
  refreshBuyerProfile,
  // Props del formulario de ofertas
  bidAmount,
  setBidAmount,
  validationError,
  setValidationError,
  handleBidSubmit,
  availableConditions,
  selectedConditions,
  setSelectedConditions,
  conditionsText,
  setConditionsText,
  // Tracking
  trackEvent,
  onScheduleVisit,
  // Handler de guardar perfil desde PropertyView
  onSaveProfile,
  // ✅ NUEVO: Handler para ofertas ciegas
  handleBlindBidSubmit,
  // ✅ NUEVO: Oferta ciega del usuario (viene de usePropertyState con tiempo real)
  userBlindOffer,
  // Notificaciones
  showNotification,
  // Traducciones dinámicas de contenido BD
  translations = null,
}) => {
  // ✅ NUEVO: Estado para controlar wizard de perfil
  const [showProfileWizard, setShowProfileWizard] = useState(false);

  // Estado para el visor de documentos
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Detectar estado actual
  const propertyStatus = getPropertyStatus(property);
  const isLastChance = propertyStatus === 'LAST_CHANCE';
  const isBlindWaiting = propertyStatus === 'BLIND_WAITING';
  const isBlindActive = propertyStatus === 'BLIND_ACTIVE';
  const isBlindPhase = isBlindWaiting || isBlindActive || (isUpcoming && !!invite?.canBlindBid);
  
  // Calcular datos para banner de última oportunidad
  const lastChanceEnd = isLastChance ? getLastChanceEndDate(property) : null;
  
  // Calcular datos para fase ciega
  const blindBiddingEnd = isBlindActive ? getBlindBiddingEndDate(property) : null;
  const canBlindBid = canUserBlindBid(invite, propertyStatus);

  // Calcular rango válido para última oportunidad
  const lastChanceBidRange = isLastChance 
    ? getLastChanceBidRange(
        userLastBid?.amount || null,
        highestBid?.amount || currentPrice,
        property.minAbsolute
      )
    : null;

  // Handler para abrir documento
  const handleOpenDocument = (doc) => {
    setSelectedDocument(doc);
    if (trackEvent) {
      trackEvent('document_view_start', {
        documentName: doc.name,
        timestamp: new Date().toISOString(),
      });
    }
  };

  // Handler para cerrar documento
  const handleCloseDocument = (viewDuration) => {
    if (trackEvent && selectedDocument) {
      trackEvent('document_view_end', {
        documentName: selectedDocument.name,
        viewDurationSeconds: viewDuration,
        timestamp: new Date().toISOString(),
      });
    }
    setSelectedDocument(null);
  };

  // Verificar si puede pujar en última oportunidad
  const canBidInLastChance = isLastChance 
    ? canUserBidInLastChance(invite?.email, propertyBids, property?.id)
    : false;

  // ✅ Determinar si se debe mostrar HighestBidCard
  const shouldShowHighestBid = () => {
    // En UPCOMING siempre mostrar (precio de salida)
    if (isUpcoming) return true;
    
    // En fase BLIND siempre mostrar (aunque no se use en esa sección)
    if (isBlindPhase) return true;
    
    // En CLOSED: ocultar a no finalistas (pueden ver la propiedad pero no el precio final)
    if (isClosed) {
      return invite?.hasAccess === true;
    }
    
    // En OPEN/LAST_CHANCE: si hay biddingStartDate, verificar si ya empezó
    if (property.biddingStartDate) {
      const now = new Date();
      const biddingStart = new Date(property.biddingStartDate);
      
      // OPEN programada (no empezó): ocultar a no finalistas
      if (biddingStart > now) {
        return invite?.hasAccess === true;
      }
      
      // OPEN activa: ya validado el acceso completo en usePropertyState,
      // aquí todos los que lleguen son finalistas, mostrar
      return true;
    }
    
    // Sin biddingStartDate, mostrar
    return true;
  };

  // ✅ NUEVO: Handler que valida perfil antes de abrir modal de visita
  const handleScheduleVisit = () => {
    const requiresProfile = property?.requireBuyerProfile !== false; // default true
    const hasProfile = !!buyerProfile;

    if (requiresProfile && !hasProfile) {
      // No tiene perfil → abrir wizard primero
      setShowProfileWizard(true);
    } else {
      // Ya tiene perfil o no es requerido → abrir Calendly
      onScheduleVisit();
    }
  };

  // ✅ NUEVO: Handler cuando se completa el perfil desde el wizard
  const handleProfileComplete = async (profileData) => {
    try {
      await onSaveProfile(profileData);
      setShowProfileWizard(false);

      // Después de crear el perfil, abrir el modal de Calendly
      onScheduleVisit();
    } catch (error) {
      console.error('Error al completar perfil:', error);
      showNotification('error', 'Error al guardar tu información', 'Por favor, intenta de nuevo.');
    }
  };

  return (
    <>
      {/* Banner de Última Oportunidad (full width) */}
      <LastChanceBanner
        isVisible={isLastChance && !isTopBidder && canBidInLastChance}
        lastChanceEnd={lastChanceEnd}
        userLastBid={userLastBid}
        highestBid={highestBid}
        currentPrice={currentPrice}
        lastChanceBidRange={lastChanceBidRange}
      />

      {/* Grid principal Media (2/3) + Sidebar (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
        
        {/* Columna principal: Video y fotos */}
        <div className="lg:col-span-2">
          <PropertyMedia
            property={property}
            trackEvent={trackEvent}
          />
        </div>

        {/* SIDEBAR: Todas las cards según estado */}
        <div className="space-y-4">
          
          {/* ========== Estado UPCOMING ========== */}
          {isUpcoming && !isBlindPhase && (
            <>
              <HighestBidCard 
                hasBids={false} 
                currentPrice={property.startPrice} 
                highestBid={null} 
              />
              <ScheduleVisitCard onScheduleVisit={handleScheduleVisit} />
              <BuyerProfileCard
                profile={buyerProfile}
                onUpdate={onSaveProfile}
              />
              <InfoCard />
            </>
          )}

          {/* ========== Estado BLIND_WAITING / BLIND_ACTIVE ========== */}
          {isBlindPhase && (
            <>
              <BlindBidCard
                property={property}
                invite={invite}
                userBlindOffer={userBlindOffer}
                canBlindBid={canBlindBid}
                bidAmount={bidAmount}
                setBidAmount={setBidAmount}
                validationError={validationError}
                setValidationError={setValidationError}
                handleBlindBidSubmit={handleBlindBidSubmit}
                conditionsText={conditionsText}
                setConditionsText={setConditionsText}
              />

              <ScheduleVisitCard onScheduleVisit={handleScheduleVisit} />

              <BuyerProfileCard
                profile={buyerProfile}
                onUpdate={onSaveProfile}
                variant="compact"
              />
            </>
          )}

          {/* ========== Estado OPEN / LAST_CHANCE ========== */}
          {/* ✅ MODIFICADO: Excluir fase BLIND para evitar renderizado duplicado */}
          {!isUpcoming && !isClosed && !isBlindPhase && (
            <>
              {shouldShowHighestBid() && (
                <HighestBidCard 
                  hasBids={hasBids} 
                  currentPrice={currentPrice} 
                  highestBid={highestBid} 
                />
              )}
              
              {userLastBid && (
                <UserLastBidCard 
                  isTopBidder={isTopBidder} 
                  userLastBid={userLastBid}
                  currentPrice={currentPrice}
                  getTimeSince={getTimeSince}
                  isClosed={isClosed}
                  isLastChance={isLastChance}
                />
              )}

              <BidFormCard 
                isExpired={isClosed}
                isUpcoming={isUpcoming}
                isLastChance={isLastChance}
                isTopBidder={isTopBidder}
                canBidInLastChance={canBidInLastChance}
                userLastBid={userLastBid}
                bidAmount={bidAmount}
                setBidAmount={setBidAmount}
                setValidationError={setValidationError}
                validationError={validationError}
                handleBidSubmit={handleBidSubmit}
                availableConditions={availableConditions}
                selectedConditions={selectedConditions}
                setSelectedConditions={setSelectedConditions}
                conditionsText={conditionsText}
                setConditionsText={setConditionsText}
                trackEvent={trackEvent}
                lastChanceBidRange={lastChanceBidRange}
                maxBid={highestBid?.amount || currentPrice}
                userBlindOffer={userBlindOffer}
                propertyStatus={propertyStatus}
              />

              {/* ✅ NUEVO: BuyerProfileCard en modo COMPACT */}
              <BuyerProfileCard
                profile={buyerProfile}
                onUpdate={onSaveProfile}
                variant="compact"
              />

              {/* ❌ OCULTADO: ScheduleVisitCard - ya no necesario en fase activa */}
              {/* ❌ OCULTADO: InfoCard - ya no necesario en fase activa */}
            </>
          )}

          {/* ========== Estado CLOSED ========== */}
          {isClosed && (
            <>
              {shouldShowHighestBid() && (
                <HighestBidCard
                  hasBids={hasBids}
                  currentPrice={currentPrice}
                  highestBid={highestBid}
                />
              )}

              {userLastBid && (
                <UserLastBidCard
                  isTopBidder={isTopBidder}
                  userLastBid={userLastBid}
                  currentPrice={currentPrice}
                  getTimeSince={getTimeSince}
                  isClosed={true}
                  isLastChance={false}
                />
              )}

              {/* Badge de oferta ciega: solo si no hubo fase abierta y el usuario tiene oferta ciega */}
              {!userLastBid && !hasBids && userBlindOffer && (
                <UserBlindOfferCard userBlindOffer={userBlindOffer} />
              )}

              <ClosedStatusCard />
              <BuyerProfileCard
                profile={buyerProfile}
                onUpdate={onSaveProfile}
              />
            </>
          )}

        </div>
      </div>

      {/* Pestaña de detalles de la propiedad: Full width */}
      <PropertyDetailsTabs
        property={property}
        onOpenDocument={handleOpenDocument}
        trackEvent={trackEvent}
        translations={translations}
      />

      {/* Modal del visor de documentos */}
      {selectedDocument && (
        <DocumentViewerModal
          document={selectedDocument}
          onClose={handleCloseDocument}
        />
      )}

      {/* ✅ NUEVO: Wizard de perfil para validación antes de Calendly */}
      {showProfileWizard && (
        <BuyerProfileWizard
          isOpen={showProfileWizard}
          onClose={() => setShowProfileWizard(false)}
          onSubmit={handleProfileComplete}
          isEditing={false}
        />
      )}
    </>
  );
};

export default PropertyContent;